import React, { useCallback, useRef, useState } from "react";
import TacticalPlayerNode from "./TacticalPlayerNode";
import BasketballCourtSVG from "../../../../components/BasketballCourtSVG";
import type { TacticalPosition } from "../../hooks/useTacticalPositions";
import {
  getBoundsForPlayer,
  getPositionGroup,
  MIN_PLAYER_SEPARATION,
} from "../../hooks/useTacticalPositions";
import type { FieldAnchor, FieldCoordinate } from "../../types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Player {
  id: string;
  full_name: string;
  short_name?: string;
  jersey_number: number;
  position: string;
}

interface TacticalFieldProps {
  homeTeamName: string;
  awayTeamName: string;
  homePlayers: Player[];
  awayPlayers: Player[];
  expelledPlayerIds?: Set<string>;
  disciplinaryStatusByPlayer?: Record<
    string,
    { foulCount: number; ejected: boolean; disqualified: boolean }
  >;
  /** Canonical positions keyed by player‑id. */
  getDisplayPosition: (
    playerId: string,
    flipSides: boolean,
  ) => TacticalPosition;
  /** Called after the user finishes dragging a player. */
  onPlayerDragEnd: (
    playerId: string,
    displayX: number,
    displayY: number,
    playerPosition: string | undefined,
    side: "home" | "away",
  ) => void;
  /** Called when a player node is clicked (no drag). */
  onPlayerClick: (
    player: Player,
    anchor: FieldAnchor,
    location: [number, number],
    side: "home" | "away",
  ) => void;
  /** Optional destination click handler (for pass/shot destination). */
  onDestinationClick?: (coordinate: FieldCoordinate) => void;
  showDestinationControls?: boolean;
  overlay?: React.ReactNode;
  flipSides?: boolean;
  /** Currently-dragged player id — used for bounds overlay + collision ghosts. */
  draggingPlayerId?: string | null;
  /** Setter so inner TacticalPlayerNode can propagate drag‑start/end. */
  onDragStart?: (playerId: string) => void;
  onDragStop?: () => void;
  /** All canonical positions — used for collision‑avoidance proximity rings. */
  allPositions?: Map<string, TacticalPosition>;
  /** When true, prevent player node dragging. */
  dragLocked?: boolean;
  /** When set, only render player nodes whose id is in this set. */
  visiblePlayerIds?: Set<string>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const resolveCourtLocation = (
  xPercent: number,
  yPercent: number,
): [number, number] => [
  Number(((xPercent / 100) * 94).toFixed(1)),
  Number(((yPercent / 100) * 50).toFixed(1)),
];

/** @deprecated alias kept for compatibility */
const resolveStatsbombLocation = resolveCourtLocation;

const buildCoordinate = (
  xPercent: number,
  yPercent: number,
  flipSides: boolean,
): FieldCoordinate => {
  const flippedX = flipSides ? 100 - xPercent : xPercent;
  const left = 4;
  const right = 96;
  const top = 4;
  const bottom = 96;
  let outOfBoundsEdge: FieldCoordinate["outOfBoundsEdge"] = null;
  const isOutOfBounds =
    flippedX < left || flippedX > right || yPercent < top || yPercent > bottom;
  if (flippedX < left) outOfBoundsEdge = "left";
  else if (flippedX > right) outOfBoundsEdge = "right";
  else if (yPercent < top) outOfBoundsEdge = "top";
  else if (yPercent > bottom) outOfBoundsEdge = "bottom";
  const cx = Math.min(100, Math.max(0, flippedX));
  const cy = Math.min(100, Math.max(0, yPercent));
  return {
    xPercent: cx,
    yPercent: cy,
    statsbomb: resolveStatsbombLocation(cx, cy),
    isOutOfBounds,
    outOfBoundsEdge,
  };
};

// ---------------------------------------------------------------------------
// Border-zone definitions (out-of-bounds destination strips)
// ---------------------------------------------------------------------------

const BORDER_COLS = 6;
const BORDER_ROWS = 4;
const BORDER_COL_PCT = 100 / BORDER_COLS; // 16.667%
const BORDER_ROW_PCT = 100 / BORDER_ROWS; // 25%

interface BorderZone {
  id: string;
  edge: "top" | "bottom" | "left" | "right";
  /** Segment index within the edge (col for H, row for V) */
  idx: number;
  /** % coordinate to pass to buildCoordinate */
  coordX: number;
  coordY: number;
}

const BORDER_ZONES: BorderZone[] = [
  // Top touchline — 6 segments aligned with zone columns
  ...Array.from(
    { length: BORDER_COLS },
    (_, i): BorderZone => ({
      id: `border-zone-top-${i}`,
      edge: "top",
      idx: i,
      coordX: (i + 0.5) * BORDER_COL_PCT,
      coordY: 0,
    }),
  ),
  // Bottom touchline
  ...Array.from(
    { length: BORDER_COLS },
    (_, i): BorderZone => ({
      id: `border-zone-bottom-${i}`,
      edge: "bottom",
      idx: i,
      coordX: (i + 0.5) * BORDER_COL_PCT,
      coordY: 100,
    }),
  ),
  // Left goal line — 4 segments aligned with zone rows
  ...Array.from(
    { length: BORDER_ROWS },
    (_, i): BorderZone => ({
      id: `border-zone-left-${i}`,
      edge: "left",
      idx: i,
      coordX: 0,
      coordY: (i + 0.5) * BORDER_ROW_PCT,
    }),
  ),
  // Right goal line
  ...Array.from(
    { length: BORDER_ROWS },
    (_, i): BorderZone => ({
      id: `border-zone-right-${i}`,
      edge: "right",
      idx: i,
      coordX: 100,
      coordY: (i + 0.5) * BORDER_ROW_PCT,
    }),
  ),
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TacticalField: React.FC<TacticalFieldProps> = ({
  homeTeamName,
  awayTeamName,
  homePlayers,
  awayPlayers,
  expelledPlayerIds,
  disciplinaryStatusByPlayer,
  getDisplayPosition,
  onPlayerDragEnd,
  onPlayerClick,
  onDestinationClick,
  showDestinationControls = false,
  overlay,
  flipSides = false,
  visiblePlayerIds,
  draggingPlayerId = null,
  onDragStart,
  onDragStop,
  allPositions,
  dragLocked = false,
}) => {
  const fieldRef = useRef<HTMLDivElement | null>(null);

  // Transient drag preview — while a player is mid‑drag we store their
  // temporary position here so the node re‑renders at the new spot without
  // committing to the canonical store yet.
  const [dragPreview, setDragPreview] = useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);

  const handleFieldClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onDestinationClick || !fieldRef.current) return;
      const rect = fieldRef.current.getBoundingClientRect();
      const xPercent = ((event.clientX - rect.left) / rect.width) * 100;
      const yPercent = ((event.clientY - rect.top) / rect.height) * 100;
      onDestinationClick(buildCoordinate(xPercent, yPercent, flipSides));
    },
    [onDestinationClick, flipSides],
  );

  const makePlayerDragEnd = useCallback(
    (playerId: string, playerPosition: string, side: "home" | "away") =>
      (displayX: number, displayY: number) => {
        setDragPreview(null);
        onDragStop?.();
        onPlayerDragEnd(playerId, displayX, displayY, playerPosition, side);
      },
    [onPlayerDragEnd, onDragStop],
  );

  const makePlayerDragMove = useCallback(
    (playerId: string) => (displayX: number, displayY: number) => {
      setDragPreview({ id: playerId, x: displayX, y: displayY });
      onDragStart?.(playerId);
    },
    [onDragStart],
  );

  const renderPlayerNode = useCallback(
    (player: Player, side: "home" | "away") => {
      const display =
        dragPreview?.id === player.id
          ? { x: dragPreview.x, y: dragPreview.y }
          : getDisplayPosition(player.id, flipSides);
      const foulStatus = disciplinaryStatusByPlayer?.[player.id];
      const isExpelled = expelledPlayerIds?.has(player.id) ?? false;

      return (
        <TacticalPlayerNode
          key={player.id}
          playerId={player.id}
          jerseyNumber={player.jersey_number}
          fullName={player.full_name}
          shortName={player.short_name}
          position={player.position}
          side={side}
          xPercent={display.x}
          yPercent={display.y}
          isExpelled={isExpelled}
          foulCount={foulStatus?.foulCount ?? 0}
          dragLocked={dragLocked}
          onClick={() => {
            if (isExpelled) return;
            const anchor: FieldAnchor = {
              xPercent: display.x,
              yPercent: display.y,
            };
            const location = resolveStatsbombLocation(display.x, display.y);
            onPlayerClick(player, anchor, location, side);
          }}
          onDragMove={makePlayerDragMove(player.id)}
          onDragEnd={makePlayerDragEnd(player.id, player.position, side)}
        />
      );
    },
    [
      dragPreview,
      getDisplayPosition,
      flipSides,
      disciplinaryStatusByPlayer,
      expelledPlayerIds,
      onPlayerClick,
      makePlayerDragMove,
      makePlayerDragEnd,
      dragLocked,
    ],
  );

  /** Whether the outer out-of-bounds frame should render */
  const showBorderFrame = Boolean(
    onDestinationClick && showDestinationControls,
  );

  /** Render a single border-zone button */
  const renderBorderButton = (zone: BorderZone) => {
    const isGoalLine = zone.edge === "left" || zone.edge === "right";
    return (
      <button
        key={zone.id}
        type="button"
        data-testid={zone.id}
        className={`flex items-center justify-center
          text-[9px] font-bold uppercase tracking-wider transition-colors cursor-pointer w-full h-full
          ${
            isGoalLine
              ? "bg-red-900/60 hover:bg-red-700/70 text-red-200 border border-red-500/40 hover:border-red-300/60"
              : "bg-amber-900/60 hover:bg-amber-700/70 text-amber-200 border border-amber-500/40 hover:border-amber-300/60"
          }`}
        onClick={(e) => {
          e.stopPropagation();
          onDestinationClick!(buildCoordinate(zone.coordX, zone.coordY, false));
        }}
        title={`Out — ${zone.edge}`}
      >
        <span
          className={`inline-flex items-center gap-1 ${
            isGoalLine ? "-rotate-90" : ""
          }`}
        >
          OUT
        </span>
      </button>
    );
  };

  /** Build the ordered array of zones for an edge, respecting flipSides */
  const getEdgeZones = (edge: BorderZone["edge"]) => {
    const zones = BORDER_ZONES.filter((z) => z.edge === edge);
    // For horizontal edges, flip column order when sides are flipped
    if (flipSides && (edge === "top" || edge === "bottom")) {
      return [...zones].reverse();
    }
    // For vertical edges, no index reversal needed (rows stay same)
    return zones;
  };

  // Build edge zone arrays once
  const topZones = getEdgeZones("top");
  const bottomZones = getEdgeZones("bottom");
  const leftEdge = flipSides ? "right" : "left";
  const rightEdge = flipSides ? "left" : "right";
  const leftZones = getEdgeZones(leftEdge);
  const rightZones = getEdgeZones(rightEdge);

  return (
    <div className="relative w-full px-2 py-2">
      {/* Outer frame: CSS grid with border-zone strips surrounding the field */}
      <div
        className="relative"
        style={
          showBorderFrame
            ? {
                display: "grid",
                gridTemplateColumns: "28px 1fr 28px",
                gridTemplateRows: "24px auto 24px",
                gap: "0px",
              }
            : {}
        }
      >
        {/* ─── Top border strip ─── */}
        {showBorderFrame && (
          <div
            className="col-start-2 col-end-3 row-start-1 row-end-2 grid rounded-t-lg overflow-hidden"
            style={{
              gridTemplateColumns: `repeat(${BORDER_COLS}, 1fr)`,
            }}
          >
            {topZones.map(renderBorderButton)}
          </div>
        )}
        {/* ─── Top-left corner ─── */}
        {showBorderFrame && (
          <div className="col-start-1 col-end-2 row-start-1 row-end-2" />
        )}
        {/* ─── Top-right corner ─── */}
        {showBorderFrame && (
          <div className="col-start-3 col-end-4 row-start-1 row-end-2" />
        )}

        {/* ─── Left border strip ─── */}
        {showBorderFrame && (
          <div
            className="col-start-1 col-end-2 row-start-2 row-end-3 grid rounded-l-lg overflow-hidden"
            style={{
              gridTemplateRows: `repeat(${BORDER_ROWS}, 1fr)`,
            }}
          >
            {leftZones.map(renderBorderButton)}
          </div>
        )}

        {/* ─── The basketball court (center cell) ─── */}
        <div
          className={`${
            showBorderFrame ? "col-start-2 col-end-3 row-start-2 row-end-3" : ""
          } relative overflow-visible`}
        >
          <div
            ref={fieldRef}
            data-testid="basketball-court"
            onClick={handleFieldClick}
            className="w-full aspect-[1.88] rounded-xl relative overflow-hidden border-4 border-white shadow-inner"
          >
            {/* Accurate NBA court (shared SVG) */}
            <BasketballCourtSVG className="absolute inset-0 w-full h-full" />

            {/* ─── Team labels ─── */}
            <div className="absolute top-2 left-2 text-white font-bold text-xs bg-black/50 px-2 py-1 rounded-md z-20">
              {flipSides ? `${awayTeamName} (Away)` : `${homeTeamName} (Home)`}
            </div>
            <div className="absolute top-2 right-2 text-white font-bold text-xs bg-black/50 px-2 py-1 rounded-md z-20">
              {flipSides ? `${homeTeamName} (Home)` : `${awayTeamName} (Away)`}
            </div>

            {/* ─── Drag bounds overlay ─── */}
            {draggingPlayerId &&
              (() => {
                // Find the dragged player's position and side
                const allPlayers = [
                  ...homePlayers.map((p) => ({ ...p, side: "home" as const })),
                  ...awayPlayers.map((p) => ({ ...p, side: "away" as const })),
                ];
                const draggedPlayer = allPlayers.find(
                  (p) => p.id === draggingPlayerId,
                );
                if (!draggedPlayer) return null;
                const bounds = getBoundsForPlayer(
                  draggedPlayer.position,
                  draggedPlayer.side,
                );
                // Convert bounds to display space (may need flip)
                const displayBounds = flipSides
                  ? {
                      xMin: 100 - bounds.xMax,
                      xMax: 100 - bounds.xMin,
                      yMin: bounds.yMin,
                      yMax: bounds.yMax,
                    }
                  : bounds;
                const group = getPositionGroup(draggedPlayer.position);
                const colorMap: Record<string, string> = {
                  guard: "rgba(59, 130, 246, 0.12)",
                  forward: "rgba(34, 197, 94, 0.12)",
                  center: "rgba(239, 68, 68, 0.12)",
                  other: "rgba(255, 255, 255, 0.08)",
                };
                const borderColorMap: Record<string, string> = {
                  guard: "rgba(59, 130, 246, 0.5)",
                  forward: "rgba(34, 197, 94, 0.5)",
                  center: "rgba(239, 68, 68, 0.5)",
                  other: "rgba(255, 255, 255, 0.3)",
                };
                return (
                  <div
                    data-testid="drag-bounds-overlay"
                    className="absolute pointer-events-none z-[5] transition-opacity duration-150"
                    style={{
                      left: `${displayBounds.xMin}%`,
                      top: `${displayBounds.yMin}%`,
                      width: `${displayBounds.xMax - displayBounds.xMin}%`,
                      height: `${displayBounds.yMax - displayBounds.yMin}%`,
                      backgroundColor: colorMap[group] ?? colorMap.other,
                      border: `2px dashed ${
                        borderColorMap[group] ?? borderColorMap.other
                      }`,
                      borderRadius: "8px",
                    }}
                  />
                );
              })()}

            {/* ─── Collision proximity rings (shown during drag) ─── */}
            {draggingPlayerId &&
              allPositions &&
              (() => {
                const rings: React.ReactNode[] = [];
                for (const [pid, pos] of allPositions) {
                  if (pid === draggingPlayerId) continue;
                  const display = flipSides
                    ? { x: 100 - pos.x, y: pos.y }
                    : pos;
                  rings.push(
                    <div
                      key={`ring-${pid}`}
                      className="absolute pointer-events-none z-[4] rounded-full border border-white/20"
                      style={{
                        left: `${display.x}%`,
                        top: `${display.y}%`,
                        width: `${MIN_PLAYER_SEPARATION * 2}%`,
                        height: `${MIN_PLAYER_SEPARATION * 2}%`,
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "rgba(255, 255, 255, 0.04)",
                      }}
                    />,
                  );
                }
                return rings;
              })()}

            {/* ─── Player Nodes ─── */}
            <div className="absolute inset-0 pointer-events-none">
              {homePlayers
                .filter((p) => !visiblePlayerIds || visiblePlayerIds.has(p.id))
                .map((p) => renderPlayerNode(p, "home"))}
              {awayPlayers
                .filter((p) => !visiblePlayerIds || visiblePlayerIds.has(p.id))
                .map((p) => renderPlayerNode(p, "away"))}
            </div>

            {/* ─── Overlay (e.g. QuickActionMenu) ─── */}
            {overlay && (
              <div
                className="absolute inset-0 z-30 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="relative h-full w-full">{overlay}</div>
              </div>
            )}
          </div>
        </div>

        {/* ─── Right border strip ─── */}
        {showBorderFrame && (
          <div
            className="col-start-3 col-end-4 row-start-2 row-end-3 grid rounded-r-lg overflow-hidden"
            style={{
              gridTemplateRows: `repeat(${BORDER_ROWS}, 1fr)`,
            }}
          >
            {rightZones.map(renderBorderButton)}
          </div>
        )}

        {/* ─── Bottom-left corner ─── */}
        {showBorderFrame && (
          <div className="col-start-1 col-end-2 row-start-3 row-end-4" />
        )}
        {/* ─── Bottom border strip ─── */}
        {showBorderFrame && (
          <div
            className="col-start-2 col-end-3 row-start-3 row-end-4 grid rounded-b-lg overflow-hidden"
            style={{
              gridTemplateColumns: `repeat(${BORDER_COLS}, 1fr)`,
            }}
          >
            {bottomZones.map(renderBorderButton)}
          </div>
        )}
        {/* ─── Bottom-right corner ─── */}
        {showBorderFrame && (
          <div className="col-start-3 col-end-4 row-start-3 row-end-4" />
        )}
      </div>
    </div>
  );
};

export default TacticalField;
